<script lang="ts" setup>
import { computed } from 'vue'
import type { WelfarePageConfig } from '../configs/types'
import WelfareGuideAddScreen from './modules/WelfareGuideAddScreen.vue'
import WelfareGuideOpen from './modules/WelfareGuideOpen.vue'
import { t } from '@/i18n/utils'

const props = defineProps<{
  config: WelfarePageConfig
  homeBgUrl: string
  btnBgColor: string
  signed: boolean
  progress: any[]
  modalSigned: boolean
  progressInfo: any
  tomorrowInfo: any
  checkInDayNum: number
  showTabbar: boolean
  tasks: any[]
  showGuidePopupAddHome: boolean
  showGuideOpen: boolean
  finishPopup: boolean
  coins: number
  completeTaskDescription: string
  userCoinBalance: number
  getBtnStatus: (item: any) => string
  onCheckIn: () => void
  handleTask: (item: any) => void
  onCheckInOk: () => void
}>()

const emit = defineEmits<{
  (e: 'update:modal-signed', value: boolean): void
  (e: 'update:show-guide-popup-add-home', value: boolean): void
  (e: 'update:show-guide-open', value: boolean): void
  (e: 'update:finish-popup', value: boolean): void
}>()

const balanceText = computed(() => (props.userCoinBalance ?? 0).toLocaleString('en-US'))

const todayIndex = computed(() => props.progress.findIndex(item => !item.isSign))
const todayPrize = computed(() => (todayIndex.value >= 0 ? props.progress[todayIndex.value]?.prizeNum : 0))

const taskChipMap: Record<number, { bg: string; color: string }> = {
  0: { bg: 'var(--novelyn-rose-faint)', color: 'var(--novelyn-rose-deep)' },
  1: { bg: 'var(--novelyn-lavender-soft)', color: 'var(--novelyn-lavender-deep)' },
  2: { bg: 'var(--novelyn-gold-soft)', color: '#9A7A1B' },
}
function taskChip(item: any) {
  return taskChipMap[item.taskType] ?? taskChipMap[0]
}
</script>

<template>
  <div class="novelyn-welfare-page min-h-100dvh pb-20">
    <!-- Cover -->
    <div class="cover w-full relative" :style="{ backgroundImage: homeBgUrl }">
      <div class="novelyn-w-head">
        <div class="novelyn-w-label">
          {{ config.texts.myWallet }}
        </div>
        <div class="novelyn-w-num">
          <img class="w-26px h-26px mr-6px" :src="config.resources.welfareGold">
          {{ balanceText }}
        </div>
      </div>

      <div class="novelyn-checkin-card bg-n-bg-card">
        <div class="flex justify-between items-center">
          <h3 class="novelyn-section-title text-16px">
            {{ config.texts.dailyCheckIn }}
          </h3>
          <span v-if="checkInDayNum > 0" class="novelyn-streak-tag">🔥 {{ checkInDayNum }} {{ config.texts.dayStraight }}</span>
        </div>
        <div class="novelyn-day-grid">
          <div
            v-for="(item, index) in progress"
            :key="item.num"
            class="novelyn-day"
            :class="{
              'is-done': item.isSign,
              'is-today': index === todayIndex,
              'is-chest': index === progress.length - 1 && !item.isSign,
            }"
          >
            <span v-if="item.isSign" class="d-ico">✓</span>
            <img v-else-if="index === progress.length - 1" class="d-ico-img" :src="config.resources.welfareBox">
            <img v-else class="d-ico-img" :src="config.resources.welfareGold">
            <span class="d-num">+{{ item.prizeNum }}</span>
          </div>
        </div>
        <a
          v-if="progress.length"
          class="novelyn-checkin-btn"
          :class="{ 'is-signed': signed }"
          @click="onCheckIn"
        >
          <span v-if="!signed">{{ config.texts.checkIn }} +{{ todayPrize }}</span>
          <span v-else-if="checkInDayNum > 0 && checkInDayNum === progress.length">{{ config.texts.tomorrowToGetMore }}</span>
          <span v-else>{{ config.texts.tomorrowCheckIn }} {{ tomorrowInfo?.prizeNum }}</span>
        </a>
      </div>
    </div>

    <div v-if="tasks && tasks.length" class="novelyn-task-card bg-n-bg-card">
      <h3 class="novelyn-section-title text-16px">
        {{ config.texts.tasks }}
      </h3>

      <div
        v-for="(item, index) in tasks"
        :key="index"
        class="novelyn-task-row"
        :class="{ 'has-line': index > 0 }"
      >
        <span class="t-ico" :style="{ background: taskChip(item).bg, color: taskChip(item).color }">
          <i-icon-park-outline-link-one v-if="item.taskType === 0" />
          <i-icon-park-outline-browser v-else-if="item.taskType === 1" />
          <i-icon-park-outline-add-web v-else />
        </span>
        <div class="flex-1 min-w-0">
          <div class="t-title">
            {{ item.taskTitle }}
          </div>
          <div class="t-coins">
            +{{ item.coins }} {{ config.texts.coins }}
          </div>
        </div>

        <span
          v-if="item.buttonState !== 1"
          class="novelyn-go-btn"
          @click="handleTask(item)"
        >
          {{ getBtnStatus(item) }}
        </span>
        <span v-else class="novelyn-ok-btn">✓ {{ t('Done') }}</span>
      </div>
    </div>

    <WelfareGuideAddScreen :value="showGuidePopupAddHome" @input="emit('update:show-guide-popup-add-home', $event)" />
    <WelfareGuideOpen :value="showGuideOpen" @input="emit('update:show-guide-open', $event)" />

    <BasicModal :value="modalSigned" @input="emit('update:modal-signed', $event)">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ config.texts.checkInGet }} <span class="text-n-primary">{{ progressInfo?.prizeNum }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="block mt-4 w-full h-9 lh-9 text-15px text-white text-center btn-solid rounded-full"
          @click="onCheckInOk"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicModal :value="finishPopup" @input="emit('update:finish-popup', $event)">
      <div class="p-5">
        <div class="text-17px lh-2em font-bold text-center text-n-text-1">
          <p>{{ completeTaskDescription }} {{ config.texts.get }} <span class="text-n-primary">{{ coins }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="block mt-4 w-full h-9 lh-9 text-15px text-white text-center btn-solid rounded-full"
          @click="emit('update:finish-popup', false)"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <BasicTabbar v-if="showTabbar" tab="rewards" />
  </div>
</template>

<style scoped>
.cover {
  background-repeat: no-repeat;
  background-position: top left;
  /* 紫色头部高度固定为 132px，与设计稿一致：紫到签到卡标题位置即结束，不再超过卡片 */
  background-size: 100% 132px;
  padding-bottom: 16px;
}

.novelyn-welfare-page {
  background: var(--novelyn-bg);
}

/* MY WALLET 头部 */
.novelyn-w-head {
  position: relative;
  z-index: 2;
  padding: 12px 20px 58px;
  color: #fff;
}

.novelyn-w-label {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  opacity: .85;
}

.novelyn-w-num {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
}

/* 签到卡（上叠头部） */
.novelyn-checkin-card,
.novelyn-task-card {
  position: relative;
  z-index: 3;
  margin-right: 16px;
  margin-left: 16px;
  padding: 18px;
  border-radius: 20px;
  box-shadow: var(--novelyn-shadow-sm);
}

.novelyn-checkin-card {
  margin-top: -40px;
}

.novelyn-task-card {
  margin-top: 14px;
}

.novelyn-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  color: var(--novelyn-ink);

  &::before {
    content: '';
    width: 4px;
    height: 16px;
    border-radius: 4px;
    background: var(--novelyn-gradient);
  }
}

.novelyn-streak-tag {
  padding: 3px 9px;
  font-size: 10px;
  font-weight: 800;
  color: var(--novelyn-rose-deep);
  white-space: nowrap;
  border: 1px solid var(--novelyn-line);
  border-radius: 999px;
  background: var(--novelyn-rose-faint);
}

/* 7 日格子 */
.novelyn-day-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin: 14px 0;
}

.novelyn-day {
  padding: 8px 2px;
  font-size: 9px;
  font-weight: 900;
  color: var(--novelyn-ink-2);
  text-align: center;
  border: 1px solid var(--novelyn-line);
  border-radius: 12px;
  background: var(--novelyn-rose-faint);

  .d-ico {
    display: block;
    height: 20px;
    font-size: 15px;
    line-height: 20px;
    margin-bottom: 2px;
  }

  .d-ico-img {
    display: block;
    width: 20px;
    height: 20px;
    margin: 0 auto 2px;
    object-fit: contain;
  }

  .d-num {
    display: block;
  }

  &.is-done {
    color: #fff;
    border-color: transparent;
    background: var(--novelyn-gradient);
  }

  &.is-today {
    box-shadow: 0 0 0 2px var(--novelyn-rose) inset;
  }

  &.is-chest {
    color: #9A7A1B;
    border-color: #EBD9A8;
    background: var(--novelyn-gold-soft);
  }
}

/* Check In 按钮 */
.novelyn-checkin-btn {
  display: block;
  width: 100%;
  height: 44px;
  font-size: 15px;
  font-weight: 800;
  line-height: 44px;
  color: #fff;
  text-align: center;
  border-radius: 999px;
  background: var(--novelyn-gradient);
  box-shadow: 0 8px 18px rgba(224, 90, 126, .32);

  &.is-signed {
    opacity: .7;
    box-shadow: none;
  }
}

/* 任务行 */
.novelyn-task-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 13px 4px;

  &.has-line {
    border-top: 1px solid var(--novelyn-line);
  }

  .t-ico {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    font-size: 16px;
    border-radius: 13px;
  }

  .t-title {
    overflow: hidden;
    font-size: 13px;
    font-weight: 800;
    color: var(--novelyn-ink);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .t-coins {
    margin-top: 2px;
    font-size: 10px;
    font-weight: 900;
    color: var(--novelyn-gold);
  }
}

.novelyn-go-btn {
  flex-shrink: 0;
  width: 64px;
  padding: 7px 0;
  font-size: 11px;
  font-weight: 900;
  color: #fff;
  text-align: center;
  border-radius: 999px;
  background: var(--novelyn-gradient);
  box-shadow: 0 5px 12px rgba(224, 90, 126, .3);
}

.novelyn-ok-btn {
  flex-shrink: 0;
  width: 64px;
  padding: 7px 0;
  font-size: 11px;
  font-weight: 900;
  color: var(--novelyn-ok);
  text-align: center;
  border-radius: 999px;
  background: var(--novelyn-rose-faint);
}
</style>
